import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../config/firebase';
import { onAuthStateChanged, sendEmailVerification } from 'firebase/auth';
import './EmailVerification.css';

const EmailVerification: React.FC = () => {
  const navigate = useNavigate();
  const [isVerified, setIsVerified] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const checkVerificationStatus = async () => {
    if (!auth.currentUser) {
      navigate('/login');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      // Force refresh the token
      await auth.currentUser.reload();
      
      if (auth.currentUser.emailVerified) {
        setIsVerified(true);
        // Wait a moment before logging out and redirecting
        setTimeout(async () => {
          try {
            await auth.signOut();
            navigate('/login', { 
              state: { 
                verificationSuccess: true,
                email: auth.currentUser?.email 
              }
            });
          } catch (err) {
            console.error('Error signing out:', err);
          }
        }, 2000);
      } else {
        setIsVerified(false);
      }
    } catch (err) {
      console.error('Error checking verification status:', err);
      setError('Failed to check email verification status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate('/login');
        return;
      }

      await checkVerificationStatus();
    });

    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setResendDisabled(false);
    }
  }, [countdown]);

  const handleResendEmail = async () => {
    if (!auth.currentUser || resendDisabled) return;

    try {
      setError('');
      setResendDisabled(true);
      setCountdown(60); // 60 second cooldown
      await sendEmailVerification(auth.currentUser);
      setEmailSent(true);
    } catch (err) {
      console.error('Error resending verification email:', err);
      setError('Failed to resend verification email. Please try again later.');
      setResendDisabled(false);
      setCountdown(0);
    }
  };

  if (loading) {
    return (
      <div className="email-verification-container">
        <div className="loading">Checking verification status...</div>
      </div>
    );
  }

  return (
    <div className="email-verification-container">
      <h2>Email Verification Required</h2>
      
      {error && <div className="error">{error}</div>}
      
      {isVerified ? (
        <div className="verification-success">
          <div className="success-message">
            <h3>✓ Email Verified!</h3>
            <p>Thank you for verifying your email address. Redirecting...</p>
          </div>
        </div>
      ) : (
        <div className="verification-pending">
          <div className="instructions">
            <h3>Please Verify Your Email</h3>
            {emailSent ? (
              <p className="success">New verification email sent! Please check your inbox.</p>
            ) : (
              <p>We've sent a verification link to your email address.</p>
            )}
            <ol>
              <li>Check your email inbox (and spam folder)</li>
              <li>Click the verification link in the email</li>
              <li>Return to this page and click the refresh button below</li>
            </ol>
          </div>
          <div className="action-buttons">
            <button 
              onClick={checkVerificationStatus} 
              className="refresh-button"
              disabled={loading}
            >
              {loading ? 'Checking...' : 'Refresh Status'}
            </button>
            <button 
              onClick={handleResendEmail} 
              className="resend-button"
              disabled={resendDisabled}
            >
              {resendDisabled 
                ? `Resend Available in ${countdown}s` 
                : 'Resend Verification Email'}
            </button>
          </div>
          <p className="help-text">
            Having trouble? Make sure to check your spam folder. If you still need help,
            please contact support.
          </p>
        </div>
      )}
    </div>
  );
};

export default EmailVerification; 